<template>
  <template v-for="group in groups" :key="group.year">
    <!-- Year Header Row -->
    <TableRow
      class="cursor-pointer transition-colors bg-muted/50 hover:bg-muted/80 border-b border-border focus-visible:bg-muted/90 outline-none"
      @click="toggleYear(group.year)"
      @keyup.enter="toggleYear(group.year)"
      @keyup.space="toggleYear(group.year)"
      role="button"
      tabindex="0"
      :aria-expanded="isYearExpanded(group.year)"
      :aria-label="`Basculer l'affichage de l'année ${group.year}`"
    >
      <TableCell colspan="6" class="py-3 px-4 font-bold select-none">
        <div class="flex items-center gap-2">
          <div class="w-6 h-6 flex items-center justify-center" aria-hidden="true">
            <ChevronDown v-if="isYearExpanded(group.year)" class="w-4 h-4 text-zinc-500" />
            <ChevronRight v-else class="w-4 h-4 text-zinc-500" />
          </div>
          <span class="text-foreground font-extrabold uppercase tracking-tight text-sm">
            Année {{ group.year }}
          </span>
          <span class="text-xs font-bold px-2 py-0.5 rounded-full bg-muted border border-border text-muted-foreground">
            {{ group.movements.length }} mouvement{{ group.movements.length > 1 ? 's' : '' }}
          </span>
        </div>
      </TableCell>
    </TableRow>

    <!-- Movements for this year -->
    <template v-if="isYearExpanded(group.year)">
      <template v-for="m in group.movements" :key="m.id">
        <!-- Main movement row -->
        <TableRow :class="{'bg-muted/30': isExpanded(m.id), 'bg-amber-500/10 border-l-4 border-l-amber-500': m.isVirtual}">
          <TableCell class="p-0 pl-1 w-10">
            <Button 
              v-if="m.subMovements && m.subMovements.length > 0" 
              variant="ghost" 
              size="sm" 
              class="h-8 w-8 p-0 focus-visible:ring-1 focus-visible:ring-indigo-500" 
              @click.stop="toggleRow(m.id)"
              :aria-expanded="isExpanded(m.id)"
              :aria-label="isExpanded(m.id) ? 'Masquer les détails' : 'Afficher les détails'"
            >
              <ChevronDown v-if="isExpanded(m.id)" class="w-3.5 h-3.5" />
              <ChevronRight v-else class="w-3.5 h-3.5" />
            </Button>
            <Button 
              v-else
              variant="ghost" 
              size="sm" 
              class="h-8 w-8 p-0 opacity-0 lg:group-hover:opacity-100 focus-visible:opacity-100" 
              @click.stop="toggleRow(m.id)"
              :aria-label="'Informations détaillées'"
            >
              <Info class="w-3 h-3 text-zinc-400" />
            </Button>
          </TableCell>
          
          <TableCell class="font-medium whitespace-nowrap text-zinc-600 tabular-nums">
            <div class="flex items-center gap-2">
              {{ formatDateFr(m.date) }}
              <History v-if="m.recurrenceGroupId" class="w-3 h-3 text-indigo-400" />
            </div>
          </TableCell>
          
          <TableCell>
            <Badge 
              class="capitalize font-bold border-none" 
              :variant="m.type === 'payment' ? 'success' : 'destructive'"
              :class="m.type === 'payment' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'"
            >
              {{ m.type === 'payment' ? 'Versement' : 'Retrait' }}
            </Badge>
          </TableCell>
          
          <TableCell>
            <!-- Virtual end-date movement -->
            <div v-if="m.isVirtual" class="flex items-center gap-2 text-amber-500 font-bold text-xs bg-amber-500/10 px-2 py-1 rounded-md w-fit border border-amber-500/20">
              <Sparkles class="w-3 h-3" /> Projection auto.
            </div>
            
            <div v-else class="flex flex-col">
              <div v-if="m.label" class="flex items-center gap-1.5 text-foreground font-bold mb-0.5">
                <Tag class="w-3.5 h-3.5 text-muted-foreground" />
                {{ m.label }}
              </div>
              <div class="text-sm text-muted-foreground font-medium">
                {{ (m.subMovements && m.subMovements.length > 0) ? (m.label ? 'Composé' : 'Mouvement Composé') : (m.category ? m.category.name : '-') }}
              </div>
              <!-- Transfer Indicator -->
              <div v-if="m.transferInvestmentName" class="mt-1 flex items-center gap-1">
                <Badge variant="outline" class="text-[10px] font-bold py-0 h-4 bg-primary/5 text-primary border-primary/20 flex items-center gap-1">
                  <RefreshCw class="w-2.5 h-2.5" />
                  {{ m.type === 'payment' ? 'Vers' : 'Depuis' }} : {{ m.transferInvestmentName }}
                </Badge>
              </div>
            </div>
          </TableCell>
          
          <TableCell class="text-right">
            <div class="font-bold tabular-nums text-base" :class="[m.type === 'payment' ? 'text-emerald-600' : 'text-red-600', { 'blur-sm select-none': isAmountsHidden }]">
              {{ m.type === 'payment' ? '+' : '-' }}{{ formatAmount(m.amount) }}€
            </div>
          </TableCell>
          
          <TableCell class="text-right pr-6">
            <div class="flex justify-end gap-1">
              <template v-if="!m.isVirtual">
                  <Button 
                    v-if="tableType === 'future'" 
                    variant="ghost" size="sm" 
                    class="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" 
                    title="Valider" 
                    aria-label="Valider le mouvement"
                    @click.stop="$emit('validate', m.id)"
                  >
                    <CheckCircle2 class="w-4 h-4" />
                  </Button>
                  
                  <Button 
                    v-if="m.recurrenceGroupId && m.recurrenceGroupId.startsWith('import_')"
                    variant="ghost" size="sm" 
                    class="h-8 w-8 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50" 
                    title="Annuler import"
                    aria-label="Annuler l'import de ce lot"
                    @click.stop="$emit('delete-import', m.recurrenceGroupId)"
                  >
                    <RotateCcw class="w-4 h-4" />
                  </Button>

                  <Button variant="ghost" size="sm" class="h-8 w-8 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50" 
                          title="Dupliquer" aria-label="Dupliquer le mouvement" @click.stop="$emit('duplicate', m)">
                    <Copy class="w-4 h-4" />
                  </Button>
                  
                  <Button variant="ghost" size="sm" class="h-8 w-8 p-0 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50" 
                          title="Éditer" aria-label="Éditer le mouvement" @click.stop="$emit('edit', m)">
                    <Pencil class="w-4 h-4" />
                  </Button>
                  
                  <Button variant="ghost" size="sm" class="h-8 w-8 p-0 text-red-400 hover:text-red-500 hover:bg-red-500/10" 
                          title="Supprimer" aria-label="Supprimer le mouvement" @click.stop="$emit('delete', m)">
                    <Trash2 class="w-4 h-4" />
                  </Button>
              </template>
              <span v-else class="text-[10px] uppercase tracking-tighter text-amber-500 font-bold bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">Auto</span>
            </div>
          </TableCell>
        </TableRow>

        <!-- Details / Compound sub-movements row -->
        <TableRow v-if="isExpanded(m.id)" class="bg-muted/30 hover:bg-muted/30">
          <TableCell colspan="6" class="p-0">
            <div class="py-4 px-12 border-l-2 border-border ml-5 my-2 space-y-3">
              <!-- If compound movement -->
              <template v-if="m.subMovements && m.subMovements.length > 0">
                <div v-for="(sub, i) in m.subMovements" :key="sub.id || i" 
                     class="flex justify-between items-center bg-card border border-border p-3 rounded-lg shadow-sm">
                  <div class="flex flex-col">
                    <span class="text-sm font-bold text-foreground">{{ sub.category ? sub.category.name : 'Sans catégorie' }}</span>
                    <div class="flex gap-2.5 mt-1">
                      <div v-if="sub.impactsCurrentValue" class="flex items-center gap-1 text-[10px] font-bold text-indigo-500 uppercase tracking-tight">
                        <TrendingUp class="w-2.5 h-2.5" /> Valorisation
                      </div>
                      <div v-if="sub.impactsInvestedAmount" class="flex items-center gap-1 text-[10px] font-bold text-emerald-500 uppercase tracking-tight">
                        <Wallet class="w-2.5 h-2.5" /> Investi
                      </div>
                    </div>
                  </div>
                  <span class="font-black tabular-nums text-foreground" :class="{ 'blur-sm select-none': isAmountsHidden }">
                    {{ formatAmount(sub.amount) }}€
                  </span>
                </div>
              </template>
              
              <!-- If simple movement (just showing impacts) -->
              <div v-else class="flex flex-col gap-2">
                <span class="text-xs font-bold text-muted-foreground uppercase tracking-widest">Impacts du mouvement</span>
                <div class="flex gap-4">
                  <div v-if="m.impactsCurrentValue" class="flex items-center gap-1.5 p-2 bg-indigo-500/10 border border-indigo-500/20 rounded-lg text-xs font-bold text-indigo-500">
                    <TrendingUp class="w-3 h-3" /> Impacte la Valorisation
                  </div>
                  <div v-if="m.impactsInvestedAmount" class="flex items-center gap-1.5 p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-xs font-bold text-emerald-500">
                    <Wallet class="w-3 h-3" /> Impacte l'Investissement
                  </div>
                  <div v-if="!m.impactsCurrentValue && !m.impactsInvestedAmount" class="text-xs italic text-muted-foreground">
                    Aucun impact financier direct sur le placement.
                  </div>
                </div>
              </div>
            </div>
          </TableCell>
        </TableRow>
      </template>
    </template>
  </template>
</template>

<script setup>
import { ref } from 'vue'
import { 
  ChevronDown, ChevronRight, History, Sparkles, Tag, Info,
  CheckCircle2, Copy, Pencil, Trash2, RotateCcw,
  TrendingUp, Wallet, RefreshCw
} from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { TableRow, TableCell } from '@/components/ui/table'

const props = defineProps({
  groups: { type: Array, required: true },
  tableType: { type: String, required: true }, // 'realized' | 'future'
  isAmountsHidden: { type: Boolean, default: false },
})

defineEmits(['validate', 'edit', 'duplicate', 'delete', 'delete-import'])

// --- Internal UI state (expand/collapse) ---
const expandedRows = ref([])
const expandedYears = ref({})

const toggleRow = (id) => {
  const idx = expandedRows.value.indexOf(id)
  if (idx > -1) expandedRows.value.splice(idx, 1)
  else expandedRows.value.push(id)
}
const isExpanded = (id) => expandedRows.value.includes(id)

const toggleYear = (year) => {
  expandedYears.value[year] = !isYearExpanded(year)
}
const isYearExpanded = (year) => {
  if (expandedYears.value[year] !== undefined) return expandedYears.value[year]
  // Default: open first group
  return props.groups.length > 0 && props.groups[0].year === year
}

// --- Helpers ---
const formatAmount = (val) => {
  const num = parseFloat(val)
  if (isNaN(num)) return '0,00'
  return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num)
}

const formatDateFr = (dateStr) => {
  if (!dateStr) return ''
  const parts = dateStr.split('-')
  if (parts.length !== 3) return dateStr
  return `${parts[2]}/${parts[1]}/${parts[0]}`
}
</script>

<style scoped>
.blur-sm {
  filter: blur(4px);
}
.tabular-nums {
  font-variant-numeric: tabular-nums;
}
</style>
